import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import packageInfo from '@package';
import { UPDATE_CHECK_INTERVAL_MS } from '@common/constants/update-check';

export function isNewerVersion(current: string, candidate: string): boolean {
    const parse = (value: string): number[] | null => {
        if (!/^\d+(?:\.\d+){1,3}$/.test(value)) return null;
        const parts = value.split('.').map(Number);
        return parts.every(Number.isSafeInteger) ? parts : null;
    };
    const installed = parse(current);
    const available = parse(candidate);
    if (!installed || !available) return false;
    for (let index = 0; index < Math.max(installed.length, available.length); index++) {
        const difference = (available[index] ?? 0) - (installed[index] ?? 0);
        if (difference !== 0) return difference > 0;
    }
    return false;
}

interface IVersionResponse {
    version: string
}

export interface IVersion {
    actual: string;
    new: string
}

@Injectable({
    providedIn: 'root'
})
export class AppVersionService {
    private readonly isVersionOld = new BehaviorSubject<IVersion | null>(null);
    private intervalId?: ReturnType<typeof setInterval>;
    private inFlight?: Promise<void>;
    private dismissedVersion?: string;

    constructor(private http: HttpClient) {
    }

    public start(): void {
        if (this.intervalId) return;
        void this.checkAppVersion();
        this.intervalId = setInterval(() => void this.checkAppVersion(), UPDATE_CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.onVisible);
        window.addEventListener('focus', this.onVisible);
    }

    public stop(): void {
        if (this.intervalId) clearInterval(this.intervalId);
        this.intervalId = undefined;
        document.removeEventListener('visibilitychange', this.onVisible);
        window.removeEventListener('focus', this.onVisible);
    }

    public dismiss(): void {
        this.dismissedVersion = this.isVersionOld.value?.new;
        this.isVersionOld.next(null);
    }

    public checkAppVersion(): Promise<void> {
        if (this.inFlight) return this.inFlight;
        this.inFlight = this.fetchVersion().finally(() => this.inFlight = undefined);
        return this.inFlight;
    }

    private readonly onVisible = (): void => {
        if (document.visibilityState === 'visible') void this.checkAppVersion();
    };

    private async fetchVersion(): Promise<void> {
        try {
            const url = `${window.location.origin}/version.json?date=${Date.now()}`;
            const { version } = await firstValueFrom(this.http.get<IVersionResponse>(url));
            if (!isNewerVersion(packageInfo.version, version)) {
                this.isVersionOld.next(null);
            } else if (version !== this.dismissedVersion) {
                this.isVersionOld.next({ actual: packageInfo.version, new: version });
            }
        } catch {
            // A version request must never interrupt the Office sidebar.
        }
    }

    public getIsVersionOld(): Observable<IVersion | null> {
        return this.isVersionOld.asObservable()
    }

}
